/** Grants.Priority — lower number = higher priority (1 = top). */

export function parseGrantPriority(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function hasGrantPriority(grant, maxPriority = 100) {
  const priority = parseGrantPriority(grant?.fields?.Priority);
  return priority != null && priority <= maxPriority;
}

export function filterTopPriorityGrants(grants, maxPriority = 100) {
  return (grants || []).filter((grant) => hasGrantPriority(grant, maxPriority));
}

export function compareGrantsByPriority(a, b) {
  const pa = parseGrantPriority(a?.fields?.Priority);
  const pb = parseGrantPriority(b?.fields?.Priority);
  if (pa != null && pb != null && pa !== pb) return pa - pb;
  if (pa != null && pb == null) return -1;
  if (pa == null && pb != null) return 1;
  return 0;
}

export function getGrantRecordIds(grant) {
  const ids = [];
  if (grant?.id) ids.push(grant.id);
  if (grant?.recordId && grant.recordId !== grant.id) ids.push(grant.recordId);
  return ids;
}

export function canToggleTopProgram(grant) {
  if (!grant || grant.fields?.isPlaceholder || grant.fields?.Technology_Upgrade) return false;
  return getGrantRecordIds(grant).length > 0;
}

/** Star empty → 1. Already ranked (2, 3…) → clear, don't overwrite. */
export function nextTopProgramPriority(currentPriority) {
  return parseGrantPriority(currentPriority) != null ? null : 1;
}

export function applyPriorityToGrant(grant, priority) {
  if (!grant) return grant;
  return {
    ...grant,
    fields: {
      ...(grant.fields || {}),
      Priority: priority,
    },
  };
}

export function applyPriorityToGrants(grants, grantId, priority) {
  if (!Array.isArray(grants) || !grantId) return grants;
  return grants.map((grant) => {
    const ids = getGrantRecordIds(grant);
    if (ids.includes(grantId)) return applyPriorityToGrant(grant, priority);
    return grant;
  });
}

export function applyPriorityOverrides(grants, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return grants || [];
  return (grants || []).map((grant) => {
    const id = getGrantRecordIds(grant).find((key) =>
      Object.prototype.hasOwnProperty.call(overrides, key)
    );
    if (!id) return grant;
    return applyPriorityToGrant(grant, overrides[id]);
  });
}

export async function patchGrantPriority(grantId, priority) {
  const apiKey = process.env.REACT_APP_AIR_KEY;
  const baseId = process.env.REACT_APP_AIR_BASEID;
  const grantTableId = process.env.REACT_APP_AIR_GRANTS;
  if (!grantId || !apiKey || !baseId || !grantTableId) {
    throw new Error("Missing grant id or Airtable config");
  }

  const res = await fetch(
    `https://api.airtable.com/v0/${baseId}/${grantTableId}/${grantId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: { Priority: priority } }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || "Failed to update grant priority");
  }

  return res.json();
}
